import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Col, Input, message, Tooltip, Spin, Modal, Table } from 'antd';
import { DownloadOutlined, EditOutlined, SaveOutlined, CloseOutlined, EyeOutlined, DeleteOutlined } from '@ant-design/icons';
import { useFileHandler } from '../../../hooks';
export const FormUploadFiles = ({ id, label, isRequired = false, folderName = '', disabled = false, readOnly = false, hidden = false, isRow = false, className = '', col = 24, responsive, value = [], onChange, accept = '.pdf,.doc,.docx', maxFiles = 10 }) => {
    const { handleGetFile, UploadFileServer, handleDownloadZip } = useFileHandler();
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [zipName, setZipName] = useState('files');
    const [isUploading, setIsUploading] = useState(false);
    const [isDetailVisible, setIsDetailVisible] = useState(false);
    const prevValueRef = useRef([]);
    // Đồng bộ state nội bộ với prop value
    useEffect(() => {
        if (JSON.stringify(value) !== JSON.stringify(prevValueRef.current)) {
            setUploadedFiles(value ?? []);
            prevValueRef.current = value ?? [];
        }
    }, [value]);
    const colProps = useMemo(() => responsive ?? { xs: col, sm: col, md: col, lg: col, xl: col, xxl: col }, [responsive, col]);
    const triggerChange = useCallback((files) => {
        setUploadedFiles(files);
        onChange?.(files);
    }, [onChange]);
    const handleFileSelect = async (e) => {
        const files = e.target.files;
        if (!files?.length)
            return;
        if (!folderName) {
            message.error('FolderName chưa được thiết lập.');
            return;
        }
        if (uploadedFiles.length + files.length > maxFiles) {
            message.error(`Tối đa ${maxFiles} file được phép upload`);
            return;
        }
        try {
            setIsUploading(true);
            const uploadFiles = Array.from(files).map((file) => {
                const rcFile = file;
                rcFile.uid = `${Date.now()}-${file.name}`;
                return {
                    uid: rcFile.uid,
                    name: rcFile.name,
                    originFileObj: rcFile
                };
            });
            const response = await UploadFileServer(uploadFiles, folderName);
            if (!response)
                throw new Error('Không có phản hồi từ server');
            const newFiles = [
                ...uploadedFiles,
                ...response.data.map((f) => ({
                    filePath: f.filePath,
                    originalName: f.originalName ?? f.name ?? 'unknown',
                    contentType: f.contentType ?? 'unknown',
                    size: f.size ?? 0,
                    uid: f.uid ?? `${Date.now()}-${Math.random()}`
                }))
            ];
            triggerChange(newFiles);
            message.success('Upload thành công!');
        }
        catch (err) {
            message.error('Upload thất bại: ' + (err.message || err));
        }
        finally {
            setIsUploading(false);
            e.target.value = '';
        }
    };
    const handleRemoveFile = (uid) => {
        const newFiles = uploadedFiles.filter((f) => f.uid !== uid);
        triggerChange(newFiles);
    };
    const handleDownload = () => {
        if (!uploadedFiles.length)
            return;
        handleDownloadZip(zipName, uploadedFiles.map((f) => f.filePath));
    };
    const fileColumns = [
        {
            title: 'Tên file',
            dataIndex: 'originalName',
            key: 'name',
            render: (text, record) => (_jsx("a", { style: { cursor: 'pointer' }, onClick: () => handleGetFile(record.filePath, record.originalName), children: text }))
        },
        { title: 'Loại file', dataIndex: 'contentType', key: 'type' },
        {
            title: 'Kích thước (KB)',
            dataIndex: 'size',
            key: 'size',
            render: (size) => (size ? (Number(size) / 1024).toFixed(2) : '0')
        },
        {
            title: 'Thao tác',
            key: 'action',
            aligin: 'center',
            render: (_, record) => !disabled && !readOnly ? (_jsx(Tooltip, { title: 'X\u00F3a', children: _jsx(DeleteOutlined, { style: { color: 'red', cursor: 'pointer' }, onClick: () => handleRemoveFile(record.uid) }) })) : null
        }
    ];
    const renderSuffix = () => {
        if (isEditing) {
            return (_jsxs(_Fragment, { children: [_jsx(Tooltip, { title: 'L\u01B0u t\u00EAn file zip', children: _jsx(SaveOutlined, { style: { cursor: 'pointer', marginRight: 8 }, onClick: (e) => {
                                e.stopPropagation();
                                setIsEditing(false);
                            } }) }), _jsx(Tooltip, { title: 'H\u1EE7y', children: _jsx(CloseOutlined, { style: { cursor: 'pointer', marginRight: 8 }, onClick: (e) => {
                                e.stopPropagation();
                                setIsEditing(false);
                                setZipName('files');
                            } }) })] }));
        }
        return (_jsxs(_Fragment, { children: [_jsx(Tooltip, { title: '\u0110\u1ED5i t\u00EAn file zip', children: _jsx(EditOutlined, { style: { cursor: 'pointer', marginRight: 8 }, onClick: (e) => {
                            e.stopPropagation();
                            setIsEditing(true);
                        } }) }), _jsx(Tooltip, { title: 'Xem chi ti\u1EBFt', children: _jsx(EyeOutlined, { style: { cursor: 'pointer', marginRight: 8 }, onClick: () => setIsDetailVisible(true) }) }), _jsx(Tooltip, { title: 'Download t\u1EA5t c\u1EA3', children: _jsx(DownloadOutlined, { style: { cursor: 'pointer', marginRight: 8 }, onClick: handleDownload }) })] }));
    };
    const content = (_jsxs("div", { style: {
            display: 'flex',
            flexDirection: isRow ? 'row' : 'column',
            alignItems: isRow ? 'center' : 'flex-start',
            gap: 8,
            width: '100%'
        }, children: [isRow && label && (_jsxs("label", { style: { whiteSpace: 'nowrap', fontWeight: 500, marginRight: 8 }, children: [label, isRequired && _jsx("span", { style: { color: 'red' }, children: " *" })] })), _jsx(Input, { readOnly: !isEditing && readOnly, placeholder: disabled || readOnly ? ' ' : 'Chọn file...', value: isEditing ? zipName : `${uploadedFiles.length} file`, onChange: (e) => isEditing && setZipName(e.target.value), onClick: () => {
                    if (!disabled && !readOnly && !isEditing)
                        document.getElementById(`hidden-file-input-${id}`)?.click();
                }, suffix: _jsxs(_Fragment, { children: [isUploading && _jsx(Spin, { size: 'small', style: { marginRight: 8 } }), renderSuffix()] }), style: { ...(isRow ? { flex: 1 } : {}) }, disabled: disabled || readOnly }), _jsx("input", { id: `hidden-file-input-${id}`, type: 'file', accept: accept, multiple: true, style: { display: 'none' }, onChange: handleFileSelect }), _jsx(Modal, { open: isDetailVisible, title: 'Chi ti\u1EBFt file', onCancel: () => setIsDetailVisible(false), footer: null, width: '70%', children: _jsx(Table, { size: 'small', pagination: false, columns: fileColumns, dataSource: uploadedFiles.map((f, i) => ({ key: f.uid || `${f.originalName}-${i}`, ...f })) }) })] }));
    return (_jsx(Col, { ...colProps, children: _jsxs("div", { className: className, style: { display: hidden ? 'none' : undefined }, children: [!isRow && label && (_jsxs("label", { style: { display: 'block', marginBottom: 10, fontWeight: 600 }, children: [label, isRequired && _jsx("span", { style: { color: 'red' }, children: " *" })] })), content] }) }));
};
